import { createI18n } from 'vue-i18n'
import { CookieClass } from '@/base/class/cookie.class'
import { COOKIE_LANG } from '@/base/const/cookies.const'
import { emitter } from '@/base/event-bus'
import { reportMessage } from '@/base/utils/errorReporting'
import { loadLanguageMessages } from '@/base/i18n/languageLoader'
import { LANG_DEFAULT, LANGUAGES, LANGUAGES_ACTIVE_LIST } from '@/base/const/lang.const'
import bundledMessages from '@/base/i18n/pl.json'

const supportedLanguages = LANGUAGES_ACTIVE_LIST.map((lang) => lang.toLowerCase())
const fallbackLanguage = LANGUAGES.en.toLowerCase()
const navigatorLanguage = navigator.language.split('-')[0].toLowerCase()
let appLanguage = CookieClass.getCookie(COOKIE_LANG)
  ? CookieClass.getCookie(COOKIE_LANG).toLowerCase()
  : navigatorLanguage
if (!supportedLanguages.includes(appLanguage)) {
  appLanguage = fallbackLanguage
  CookieClass.setCookie(COOKIE_LANG, appLanguage.toUpperCase())
}
/**
 * Język, w którym aplikacja WSTAJE - zanim backend powie, jaki język ma transakcja.
 * To stała policzona raz, przy imporcie modułu, więc NIE nadaje się na „język bieżący":
 * `setLanguage` (store/modules/lang.js) zmienia locale i cookie, ale tej wartości już nie ruszy.
 */
export const currentLang = appLanguage

/**
 * Ostatnio ZAŻĄDANY język - jedyne źródło prawdy o tym, co aplikacja ma pokazywać.
 * Aktualizowany na wejściu do `loadLanguage`, czyli ZANIM paczka tłumaczeń się wczyta.
 *
 * Powód (zgłoszenie z 31.07.2026, transakcja A75NFE14A9): guard routera wołał `loadLanguage(currentLang)`
 * przy KAŻDEJ nawigacji, a `currentLang` jest zamrożony na języku przeglądarki. Użytkownik z nagłówkiem
 * `accept-language: en-US,…` dostawał poprawnie polski paywall (język z transakcji), po czym pierwsza
 * nawigacja w obrębie SPA - `SessionTimer.vue` → `router.push('/session-timeout')` po wygaśnięciu sesji,
 * `Start.vue` → `PayerPaywall` / `RegulationsApprovalStart` - przestawiała locale z powrotem na `en`.
 * Objaw znikał po przeładowaniu strony, bo wtedy `currentLang` czytał już cookie ustawione przez
 * `setLanguage`. Guard używa teraz `loadCurrentLanguage()`, więc nawigacja jest bezstratna.
 */
let requestedLang = appLanguage

/**
 * Język WBUDOWANY w główny bundel — jedyny, który nie zależy od sieci.
 *
 * Powód (pomiar 17.09.2026, `doc/robocze/sentry-pomiary.md`): grupa #4203 raportuje dopiero, gdy padnie
 * `import()` chunka językowego ORAZ fallbackowy fetch — i takich zdarzeń było **405 na dobę u 387 osób**
 * (0,4 % sesji), z czego **23/25 w próbce dotyczyło `lang: pl`**. Dokument był przy tym widoczny i online,
 * więc nie chodziło o rozbiórkę strony, tylko o urwany transfer u klienta. Bez paczki w bundlu ci
 * użytkownicy dostawali bramkę z surowymi ścieżkami kluczy (`payerPaywall.header`), bo `createI18n`
 * nie miał `fallbackLocale`.
 *
 * Koszt: ~13 kB gzip w bundlu bazowym (współdzielonym przez wszystkie konteksty), raz.
 * Zysk: dla `pl` tłumaczenia są na miejscu ZANIM ruszy jakiekolwiek żądanie, a dla pozostałych
 * 19 języków nieudane pobranie schodzi do polskiego zamiast do surowych kluczy.
 */
const BUNDLED_LANG = LANG_DEFAULT.toLowerCase()

const i18n = createI18n({
  legacy: true,
  locale: currentLang,
  /*
   * Paczka wbudowana siedzi w `messages` od chwili utworzenia instancji, więc `loadLanguage(BUNDLED_LANG)`
   * jest dla użytkownika bezstratne nawet wtedy, gdy padnie i import, i fallback. Pozostałe 19 paczek
   * dokłada `setLocaleMessage` po wczytaniu — tego nie wolno zamienić na eager import wszystkich języków
   * (20 × ~48 kB w bundlu startowym).
   */
  messages: { [BUNDLED_LANG]: bundledMessages },
  /*
   * ⚠️ `fallbackLocale` schodzi do polskiego dla kluczy, których NIE MA w locale bieżącym — w tym dla
   * całej brakującej paczki, gdy jej pobranie padło. To siatka bezpieczeństwa, a NIE zgoda na niekompletne
   * tłumaczenia: klucz dodany do `pl.json` nadal musi trafić w tym samym commicie do pozostałych
   * 19 paczek, bo inaczej użytkownik zobaczy polski tekst w obcojęzycznym interfejsie — objaw
   * cichszy niż surowa ścieżka klucza, więc trudniejszy do wychwycenia okiem.
   * Pilnuje tego `tests/unit/i18n/messagesParity.spec.js` i to on, nie ten mechanizm, jest gwarancją.
   */
  fallbackLocale: BUNDLED_LANG,
  globalInjection: true,
  // legacy: true czyta WYŁĄCZNIE nazwy z Options API (convertComposerOptions w vue-i18n) -
  // missingWarn / fallbackWarn / warnHtmlMessage podane wprost są tu ignorowane.
  // HTML w komunikatach jest zamierzony (linki w regulaminach), więc tę gałąź wyciszamy;
  // brakujące klucze i fallbacki zostawiamy widoczne - na produkcji vue-i18n wycina je z bundla,
  // a na dev/tst/stg to jedyny sygnał, że jakiegoś tłumaczenia nie ma.
  warnHtmlInMessage: 'off',
})

export default i18n

const languageModules = import.meta.glob('./*.json')
const languageAssetUrls = import.meta.glob('./*.json', {
  query: '?url',
  import: 'default',
  eager: true,
})

/*
 * MECHANIZM ODTWORZONY 11.08.2026 (Playwright WebKit + Chromium; pełny raport i wyniki
 * reprodukcji: doc/robocze/webkit-import-chunkow.md, sekcja 10).
 *
 * Po nieudanym dynamicznym `import()` przeglądarka zapamiętuje porażkę w module map (wymóg
 * specyfikacji HTML): każdy kolejny `import()` TEGO SAMEGO specifiera odrzuca natychmiast,
 * BEZ żadnego żądania sieciowego, do końca życia strony. Zmierzone w obu silnikach: po
 * padniętym imporcie ponowienie nie wysyła requestu; import z innym query (inny specifier)
 * wysyła i - gdy sieć wróciła - przechodzi.
 *
 * To unieważnia interpretację wycofanego ponowienia z 10-11.08.2026 (`importLanguageModule`,
 * 300 ms): wołało ten sam loader z import.meta.glob, więc `extra.retries: 1` znaczyło tylko,
 * że odczytano porażkę z cache module map. Hipoteza „chwilowa awaria sieci" NIE została tym
 * pomiarem obalona - pomiar nie był w stanie jej dotknąć. Drugi wynik reprodukcji: przy
 * zerwanym połączeniu WebKit sam ponawia żądanie do 3× zanim import padnie (Chromium 2×),
 * a przy zerwaniu w połowie treści nie ponawia wcale.
 *
 * Obecne zabezpieczenie (languageLoader.js) omija oba cache: po padniętym imporcie pobiera
 * surowy JSON tłumaczeń fetch-em (`cache: 'reload'`) spod adresu z `import.meta.glob(...,
 * query: '?url')`. UWAGA: adres (`/web/assets/<lang>.<hash>.json`) działa tylko dzięki regule
 * `RewriteRule web/assets/` w public/.htaccess - bez niej origin oddaje payment.html (200
 * text/html) i fallback pada na parsowaniu (sekcja 10.6 raportu; test regresyjny:
 * tests/unit/i18n/fallbackRouting.spec.js). Fetch nie dotyka module map, daje pełną diagnostykę
 * (URL, status HTTP, content-type - `import()` w WebKicie nie podaje żadnego) i raportuje wyłącznie
 * PORAŻKĘ (tag `i18nFallback: failed`), z URL-em, statusem, `navigator.onLine`, `visibilityState`
 * i `bundledFallback`. Sukces (`recovered`) nie jest raportowany od 18.08.2026 - pomiar zakończony,
 * opis w languageLoader.js. Raportuje wyłącznie languageLoader - catch przy starcie (niżej) celowo
 * NIE raportuje drugi raz, bo podwajałby porażki.
 *
 * Przeładowanie przy starcie (`reloadOnceOnStartupFailure`) pozostaje wycofane: kosztowało
 * ~731 wymuszonych przeładowań dziennie, a jego skuteczności nie dało się zmierzyć (raport
 * leciał przed przeładowaniem). Fallback fetch ratuje sesję bez przeładowania. Nie dodawaj
 * ponowienia `import()` tego samego specifiera - to no-op, patrz wyżej.
 */
export function loadLanguage(lang) {
  return new Promise((resolve, reject) => {
    const loadModule = languageModules[`./${lang}.json`]

    if (!loadModule) {
      // język spoza bundla (np. sklep podał ?lang=xx) - użytkownik dostaje fallback
      reportMessage('i18n: brak tłumaczeń dla języka', {
        tags: { flow: 'i18n' },
        extra: { lang },
      })
      reject(new Error(`language ${lang} not found`))
      return
    }

    // Po sprawdzeniu istnienia paczki: język spoza bundla nie może przestawić żądania.
    requestedLang = lang

    loadLanguageMessages(loadModule, languageAssetUrls[`./${lang}.json`], lang)
      .then((messages) => {
        i18n.global.setLocaleMessage(lang, messages)

        // Wynik NIEAKTUALNEGO żądania tylko cache'ujemy - locale zostaje przy tym, o co poproszono
        // ostatnio. Bez tego wolno wczytująca się paczka nadpisywała język zażądany później:
        // startowy `loadLanguage(currentLang)` leci przy imporcie modułu i biegnie równolegle
        // z `setPaymentLanguage` (język z transakcji, po odpowiedzi `/www/start`), więc przy wolnym
        // pobraniu chunka startowego wygrywał język przeglądarki - mimo że nikt go nie wybierał.
        if (requestedLang === lang) {
          i18n.global.locale = lang
        }

        // Zdarzenie leci ZAWSZE, także dla nieaktualnego żądania: nasłuchujący (`AppHeader`,
        // `Error/Template`) czytają z niego tylko „tłumaczenia są już wczytane" i renderują
        // z bieżącego locale, którego stary wynik i tak nie ruszył. Gdyby zdarzenie było pod tym
        // samym warunkiem, sekwencja „startowa paczka wyprzedzona przez język z transakcji, po czym
        // ten drugi pada" nie wyemitowałaby go nigdy - a `AppHeader` chowa całą zawartość nagłówka
        // do czasu pierwszego zdarzenia.
        emitter.emit('language-loaded')
        resolve()
      })
      .catch(reject)
  })
}

/**
 * Wczytuje ponownie AKTUALNIE zażądany język. Wejście dla guarda routera: nawigacja ma upewnić się,
 * że paczka tłumaczeń jest na miejscu, a nie narzucać język. Patrz komentarz przy `requestedLang`.
 *
 * @returns {Promise<void>}
 */
export function loadCurrentLanguage() {
  return loadLanguage(requestedLang || LANG_DEFAULT.toLowerCase())
}

loadLanguage(currentLang)
  .then(() => {
    i18n.locale = currentLang
  })
  .catch(() => {
    // Startowy język się nie wczytał. Od 17.09.2026 NIE znaczy to już surowych kluczy: `pl` siedzi
    // w bundlu, a `fallbackLocale` schodzi do niego dla każdego innego locale - użytkownik dostaje
    // czytelny interfejs (po polsku), a nie ścieżki kluczy.
    // Bez ponownego raportu: languageLoader zaraportował już wynik (recovered/failed),
    // a drugi raport tutaj podwajałby porażki i przekłamywał proporcję recovered/failed
  })
